import { ChangeEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import LanguageSelectField from 'components/forms/languageSelectField/LanguageSelectField';
import { getLanguageName } from 'i18n/i18n.utils';
import { LanguageCode } from 'i18n/internationalization.types';
import { UsageType } from 'models/automation/Common';
import { orderBy } from 'utils/arrayUtils';
import useAvailableLanguages from './useAvailableLanguages';

const TemplateModalLanguageSelectField = ({
  usageType,
  isDisabled = false,
}: {
  usageType: UsageType;
  isDisabled?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const {
    setValue,
    getValues,
    formState: { dirtyFields },
  } = useFormContext();
  const { availableLanguages, isLoading } = useAvailableLanguages(usageType);

  const isCustomTemplate = usageType === UsageType.CUSTOM;

  const languageOptions = availableLanguages.map((languageCode) => ({
    label: getLanguageName(i18n.language, languageCode),
    value: languageCode,
  }));

  const options = orderBy(languageOptions, [{ field: 'label', order: 'asc' }]);

  const onChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value: nextSelectedLanguage } = event.target;
    const { language, locales } = getValues();
    const previousSelectedLanguage = language as LanguageCode;

    // It should not dirty the form so just changing the language doesn't enable the save button
    setValue('language', nextSelectedLanguage, { shouldDirty: false });

    const isCurrentLanguageChanged =
      dirtyFields.locales?.[previousSelectedLanguage];
    const currentContent =
      locales[previousSelectedLanguage]?.content_htmlContent;

    // Updates original `content` for the locale since `content_htmlContent` derives from it.
    if (isCurrentLanguageChanged && currentContent) {
      setValue(
        `locales.${previousSelectedLanguage}.content`,
        currentContent as never,
      );
    }
  };

  return (
    <LanguageSelectField
      colSmInput={isCustomTemplate ? 9 : 8}
      colSmLabel={isCustomTemplate ? 3 : 4}
      isLoading={isLoading}
      label={
        isCustomTemplate
          ? t('pageAutomations.customTemplates.modal.language')
          : t('pageAutomations.systemTemplates.modal.language')
      }
      name="language"
      onChange={onChangeHandler}
      options={options}
      disabled={isDisabled}
    />
  );
};

export default TemplateModalLanguageSelectField;

TemplateModalLanguageSelectField.defaultProps = {
  isDisabled: false,
};
